import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  NgModule,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { ScrollToModule, ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { fadeInOnEnterAnimation, fadeOutOnLeaveAnimation } from 'angular-animations';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-scroll-top',
  templateUrl: './scroll-top.component.html',
  styleUrls: ['./scroll-top.component.scss'],
  animations: [fadeInOnEnterAnimation({ duration: 400 }), fadeOutOnLeaveAnimation({ duration: 400 })],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollTopComponent implements OnDestroy {
  @Input() containerId?: string = 'mainContent';
  @Input() scrollDestination = this.document.getElementsByTagName('main')[0];
  @Input() scrollOffset = 0;

  @ViewChild('scrollTop') scrollTop!: ElementRef;

  focusable$ = new BehaviorSubject<boolean>(false);
  isShown$ = new BehaviorSubject<boolean>(false);

  scrollTopBehavior$!: Observable<any>;

  get isMobileOrTabletPortrait$(): Observable<boolean> {
    return this.breakpointsService.isMobileAndTabletPortrait$;
  }

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly scrollToService: ScrollToService,
    @Inject(DOCUMENT) private readonly document: any
  ) {}

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    const container = this.document.getElementById(this.containerId);
    this.isShown$.next(container.getBoundingClientRect().top < 0);
  }

  restoreFocusTop(): void {
    const elmToFocus = this.document.querySelector(this.scrollDestination).querySelector('button, a');
    if (elmToFocus) {
      elmToFocus.focus();
    }
  }

  scrollToTop(): void {
    this.scrollToService
      .scrollTo({
        target: this.scrollDestination,
        offset: this.scrollOffset,
        easing: 'easeOutElastic',
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.restoreFocusTop();
        },
        error: () => {
          // catch error instead of throwing an error
        },
      });
  }

  ngOnDestroy(): void {
    this.focusable$.complete();
    this.isShown$.complete();
  }
}

@NgModule({
  imports: [CommonModule, ScrollToModule, MatIconModule],
  declarations: [ScrollTopComponent],
  exports: [ScrollTopComponent],
})
export class ScrollTopModule {}
